import React from 'react';
import {
  CheckCircle2, XCircle, Image as ImageIcon, Users, Calendar, FileText,
  Mail, Sparkles, BarChart3, AlertTriangle,
} from 'lucide-react';

const TOOL_ICONS = {
  'members.stats':                     Users,
  'members.search':                    Users,
  'members.incomplete_profiles':       Users,
  'members.find_duplicates':           Users,
  'members.unactivated':               Users,
  'members.businesses_without_reps':   Users,
  'events.list':                       Calendar,
  'events.details':                    Calendar,
  'events.needing_attention':          AlertTriangle,
  'events.create_draft':               Calendar,
  'events.improve_description':        Sparkles,
  'events.publish':                    Calendar,
  'blog.list':                         FileText,
  'blog.create_draft':                 FileText,
  'blog.improve':                      Sparkles,
  'blog.publish':                      FileText,
  'communications.audience_estimate':  Mail,
  'communications.campaigns_recent':   Mail,
  'communications.draft_campaign':     Mail,
  'communications.send_campaign':      Mail,
  'image.generate_for_event':          ImageIcon,
  'image.generate_for_blog':           ImageIcon,
  'image.generate_for_campaign':       ImageIcon,
  'image.generate_freeform':           ImageIcon,
  'reports.platform_overview':         BarChart3,
};

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const StatTile = ({ label, value }) => (
  <div className="bg-slate-50 rounded-lg px-3 py-2 border border-slate-200">
    <div className="text-[10.5px] uppercase tracking-wide text-slate-500 font-semibold">{label}</div>
    <div className="text-base font-bold text-[#1a1a1a] mt-0.5">
      {typeof value === 'number' ? value.toLocaleString() : (value ?? '—')}
    </div>
  </div>
);

const MiniTable = ({ rows, columns, testid }) => (
  <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white" data-testid={testid}>
    <table className="w-full text-[12px]">
      <thead className="bg-slate-50">
        <tr>
          {columns.map((c) => (
            <th key={c.key} className="text-left px-3 py-2 font-semibold text-slate-500 uppercase tracking-wide text-[10.5px]">{c.label}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.slice(0, 15).map((r, i) => (
          <tr key={i} className="border-t border-slate-100 hover:bg-slate-50">
            {columns.map((c) => (
              <td key={c.key} className="px-3 py-2 text-slate-700">
                {c.render ? c.render(r) : (r[c.key] ?? '—')}
              </td>
            ))}
          </tr>
        ))}
        {rows.length > 15 && (
          <tr>
            <td className="px-3 py-2 text-slate-400 text-[11px]" colSpan={columns.length}>
              …and {rows.length - 15} more
            </td>
          </tr>
        )}
      </tbody>
    </table>
  </div>
);

/**
 * ToolResultCard — renders the structured output of a tool call inline
 * with the chat conversation. Each tool has a bespoke renderer that
 * surfaces the useful bits without overwhelming the user.
 */
export const ToolResultCard = ({ card }) => {
  if (!card) return null;
  const { tool_id, ok, result, error } = card;
  const Icon = TOOL_ICONS[tool_id] || Sparkles;

  if (!ok) {
    return (
      <div
        className="rounded-xl border border-rose-200 bg-rose-50 p-3 mt-2 flex items-start gap-2"
        data-testid={`ai-tool-error-${tool_id}`}
      >
        <XCircle className="w-4 h-4 text-rose-500 flex-shrink-0 mt-0.5" />
        <div className="text-[12.5px] text-rose-800">
          <div className="font-semibold">{tool_id}</div>
          <div className="opacity-90">{error || 'Tool failed.'}</div>
        </div>
      </div>
    );
  }

  // Confirmation card is rendered separately by the parent — skip here
  if (card.requires_confirmation) return null;

  const header = (
    <div className="flex items-center gap-2 mb-2">
      <div className="w-6 h-6 rounded-md bg-[#002249]/10 text-[#002249] flex items-center justify-center">
        <Icon className="w-3.5 h-3.5" />
      </div>
      <div className="text-[11px] uppercase font-bold tracking-wider text-slate-500">{tool_id}</div>
      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 ml-auto" />
    </div>
  );

  const wrapper = (body) => (
    <div
      className="rounded-xl border border-slate-200 bg-white p-3 mt-2"
      data-testid={`ai-tool-result-${tool_id}`}
    >
      {header}
      {body}
    </div>
  );

  // ─── Members stats ─────────────────────────────────────────────────
  if (tool_id === 'members.stats') {
    return wrapper(
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <StatTile label="Members" value={result.members_total} />
        <StatTile label="Businesses" value={result.members_business} />
        <StatTile label="Individuals" value={result.members_individual} />
        <StatTile label="Logged in" value={result.members_logged_in_at_least_once} />
        <StatTile label="Never logged in" value={result.members_never_logged_in} />
        <StatTile label="Leadership" value={result.leadership_count} />
        <StatTile label="Staff" value={result.staff_count} />
      </div>,
    );
  }

  // ─── Members search / incomplete / unactivated / biz-without-reps ──
  if ([
    'members.search',
    'members.incomplete_profiles',
    'members.unactivated',
    'members.businesses_without_reps',
  ].includes(tool_id)) {
    const rows = result?.members || [];
    return wrapper(
      <>
        <div className="text-xs text-slate-500 mb-2">{result?.count || 0} result(s)</div>
        <MiniTable
          testid={`ai-table-${tool_id}`}
          rows={rows}
          columns={[
            { key: 'name', label: 'Name' },
            { key: 'email', label: 'Email' },
            { key: 'is_business', label: 'Type', render: (r) => r.is_business ? 'Business' : 'Individual' },
            { key: 'last_login_at', label: 'Last login', render: (r) => r.last_login_at ? new Date(r.last_login_at).toLocaleDateString() : '—' },
            ...(tool_id === 'members.incomplete_profiles'
              ? [{ key: 'missing_fields', label: 'Missing', render: (r) => (r.missing_fields || []).join(', ') }]
              : []),
          ]}
        />
      </>,
    );
  }

  // ─── Duplicates ────────────────────────────────────────────────────
  if (tool_id === 'members.find_duplicates') {
    const groups = result?.groups || [];
    return wrapper(
      <>
        <div className="text-xs text-slate-500 mb-2">{result?.count || 0} potential group(s)</div>
        <div className="space-y-2">
          {groups.slice(0, 10).map((g, i) => (
            <div key={i} className="rounded-lg border border-amber-200 bg-amber-50 p-2 text-[12px]">
              <div className="text-amber-900 font-semibold mb-1">
                {g.reason === 'same_name' ? 'Same name' : 'Same email alias'} · {g.match}
              </div>
              <div className="space-y-0.5">
                {g.members.map((m, j) => (
                  <div key={j} className="text-slate-700">
                    {m.name} <span className="text-slate-500">({m.email})</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </>,
    );
  }

  // ─── Events ────────────────────────────────────────────────────────
  if (tool_id === 'events.list') {
    const rows = result?.events || [];
    return wrapper(
      <>
        <div className="text-xs text-slate-500 mb-2">
          {rows.length} event(s) · scope: {result?.scope}
        </div>
        <MiniTable
          testid={`ai-table-${tool_id}`}
          rows={rows}
          columns={[
            { key: 'title', label: 'Title' },
            { key: 'date', label: 'Date' },
            { key: 'location', label: 'Location' },
            { key: 'image_url', label: 'Art', render: (r) => r.image_url ? '✓' : '—' },
          ]}
        />
      </>,
    );
  }

  if (tool_id === 'events.needing_attention') {
    const rows = result?.events || [];
    return wrapper(
      <>
        <div className="text-xs text-slate-500 mb-2">
          {rows.length} event(s) need attention
        </div>
        <div className="space-y-1.5">
          {rows.slice(0, 10).map((e) => (
            <div key={e.id} className="rounded-lg border border-slate-200 p-2 text-[12px] flex items-center gap-2">
              <div className="flex-1">
                <div className="font-semibold text-[#1a1a1a]">{e.title}</div>
                <div className="text-slate-500 text-[11px]">{e.date} · {(e.issues || []).join(', ')}</div>
              </div>
            </div>
          ))}
        </div>
      </>,
    );
  }

  if (tool_id === 'events.details') {
    const e = result?.event || {};
    return wrapper(
      <div>
        <div className="font-bold text-[#1a1a1a]">{e.title || 'Event'}</div>
        <div className="text-xs text-slate-500">
          {e.date} {e.time} · {e.location}
        </div>
        {e.image_url && (
          <img
            alt=""
            src={e.image_url?.startsWith('/api') ? `${BACKEND}${e.image_url}` : e.image_url}
            className="w-full h-40 object-cover rounded-lg mt-2 border"
          />
        )}
        <div className="text-[12px] text-slate-700 mt-2 line-clamp-5">{e.description || '—'}</div>
        {typeof e.registration_count === 'number' && (
          <div className="text-xs text-slate-500 mt-1">Registrations: {e.registration_count}</div>
        )}
      </div>,
    );
  }

  if (tool_id === 'events.create_draft' || tool_id === 'blog.create_draft') {
    const payload = result?.event || result?.post || {};
    return wrapper(
      <div className="text-[12.5px] text-slate-700">
        <div className="font-semibold text-emerald-700">Draft created</div>
        <div className="mt-1"><span className="text-slate-500">Title:</span> {payload.title}</div>
        {payload.date && <div><span className="text-slate-500">Date:</span> {payload.date}</div>}
        {payload.location && <div><span className="text-slate-500">Location:</span> {payload.location}</div>}
        <div className="text-[11px] text-slate-500 mt-1">
          id: <span className="font-mono">{payload.id}</span>
        </div>
      </div>,
    );
  }

  if (tool_id === 'blog.list') {
    const rows = result?.posts || [];
    return wrapper(
      <MiniTable
        testid={`ai-table-${tool_id}`}
        rows={rows}
        columns={[
          { key: 'title', label: 'Title' },
          { key: 'category', label: 'Cat' },
          { key: 'is_published', label: 'Pub', render: (r) => r.is_published ? '✓' : '—' },
        ]}
      />,
    );
  }

  // ─── Communications ───────────────────────────────────────────────
  if (tool_id === 'communications.audience_estimate') {
    return wrapper(
      <div className="grid grid-cols-2 gap-2">
        <StatTile label="Audience" value={result.label || result.audience} />
        <StatTile label="Eligible" value={result.eligible_recipients} />
      </div>,
    );
  }

  if (tool_id === 'communications.campaigns_recent') {
    const rows = result?.campaigns || [];
    return wrapper(
      <MiniTable
        testid={`ai-table-${tool_id}`}
        rows={rows}
        columns={[
          { key: 'subject', label: 'Subject' },
          { key: 'status', label: 'Status' },
          { key: 'sent', label: 'Sent' },
          { key: 'audience_label', label: 'Audience' },
        ]}
      />,
    );
  }

  if (tool_id === 'communications.draft_campaign') {
    return wrapper(
      <div>
        <div className="text-[11px] uppercase font-semibold text-slate-500 mb-1">Draft subject</div>
        <div className="font-bold text-[#1a1a1a] mb-2">{result.subject}</div>
        <div className="text-[11px] uppercase font-semibold text-slate-500 mb-1">Body</div>
        <div
          className="text-[12.5px] text-slate-700 max-h-56 overflow-y-auto border rounded-md p-2 bg-slate-50"
          dangerouslySetInnerHTML={{ __html: result.body_html || result.body_text }}
        />
      </div>,
    );
  }

  // ─── Image generation ──────────────────────────────────────────────
  if (tool_id?.startsWith('image.')) {
    const url = result?.url ? (result.url.startsWith('/api') ? `${BACKEND}${result.url}` : result.url) : '';
    return wrapper(
      <div>
        {url && (
          <img
            alt="AI generated"
            src={url}
            className="w-full h-auto rounded-lg border border-slate-200 bg-slate-50"
          />
        )}
        <div className="text-[11px] text-slate-500 mt-1.5">
          Generated with {result?.provider} · {result?.mime_type}
        </div>
      </div>,
    );
  }

  // ─── Reports / overview ────────────────────────────────────────────
  if (tool_id === 'reports.platform_overview') {
    const r = result || {};
    return wrapper(
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <StatTile label="Members" value={r.members?.total} />
        <StatTile label="Businesses" value={r.members?.business} />
        <StatTile label="Upcoming" value={r.events?.upcoming} />
        <StatTile label="Past events" value={r.events?.past} />
        <StatTile label="Posts live" value={r.blog?.published} />
        <StatTile label="Drafts" value={r.blog?.draft} />
        <StatTile label="Scheduled campaigns" value={r.campaigns?.scheduled} />
      </div>,
    );
  }

  // ─── Fallback — compact JSON ───────────────────────────────────────
  return wrapper(
    <pre className="text-[11px] text-slate-600 whitespace-pre-wrap max-h-56 overflow-y-auto bg-slate-50 rounded p-2 border border-slate-100">
      {JSON.stringify(result, null, 2).slice(0, 1500)}
    </pre>,
  );
};

export default ToolResultCard;
